/**
 * Schema-driven LN2 message selector — device-type-agnostic.
 *
 * Single searchable dropdown with all messages formatted as "Category::Name"
 * (e.g., "Status::HighSpeedSequence", "Traffic::CarCall", "Fault::MotorOverTemp").
 *
 * Typing filters across all categories:
 *   - "HighSpeed" → shows Status::HighSpeedSequence and any other matches
 *   - "Fault::" → shows only Fault messages
 *   - "CarCall" → shows Traffic::CarCall, Traffic::RearCarCall, etc.
 *
 * A separate Normal/Valued variant toggle accompanies the main dropdown.
 *
 * The elevator/escalator device type is NOT selected here — it is determined
 * later when the adapter is assigned to a channel → bank with a known type.
 *
 * Value format: composite key string "<category>:<variant>:<enumValue>"
 * e.g., "status:normal:7" for Status::Power (Normal)
 */
import React, { useMemo, useState } from "react";
import { Select } from "antd";
import {
  LN2_MESSAGE_CATEGORIES,
  parseCompositeKey,
  getDisplayName,
  MessageVariant,
  Ln2MessageFilter,
  ln2MessageFilterToKeySet,
} from "../../../flatbuffers/types/ln2MessageOptions";

interface SelectLn2MessageProps {
  /** Composite key value, e.g., "status:normal:7" */
  value: string;
  /** Called when the user selects or changes a message */
  onChange: (key: string) => void;
  /** Disable the selects */
  disabled?: boolean;
  /** Show as read-only text instead of selects */
  displayOnly?: boolean;
  /** When set, only show categories whose categoryKey is in this list */
  categoryFilter?: string[];
  /**
   * Typed filter using FlatBuffer MessageContent → inner enum values.
   * Only messages matching the filter appear in the dropdown.
   */
  messageFilter?: Ln2MessageFilter;
  /** Render in compact mode with tighter spacing */
  compact?: boolean;
  /**
   * Lock the Normal/Valued toggle to "normal" and disable it, independent of
   * `disabled` (which still leaves the message dropdown itself usable).
   *
   * Use this when the caller never reads `parseCompositeKey(...).variant` —
   * offering a choice that is silently discarded is worse than not offering
   * it. Notifications are the first such caller (LIFT-940): the backend has
   * no field for the variant, so it never survives a POST/PUT round-trip,
   * and every persisted message_id decodes back to "normal" regardless of
   * what was picked (see decodeFlattenedMessage in ln2MessageOptions.ts).
   */
  disableVariantToggle?: boolean;
}

/**
 * Pre-built flat list of all message options across all categories.
 * Each option's value is "category:enumValue" (variant is appended at selection time).
 * Labels are "Category::EnumName" for searchability.
 */
interface MessageSelectOption {
  /** "category:enumValue" — variant is added when composing the full key */
  categoryEnumKey: string;
  /** Display label: "Status::HighSpeedSequence" */
  label: string;
  /** Category label for grouping */
  group: string;
}

const ALL_MESSAGE_OPTIONS: MessageSelectOption[] = LN2_MESSAGE_CATEGORIES.flatMap((cat) => {
  // Deduplicate: each enum value appears once (not doubled by normal/valued)
  const seen = new Set<number>();
  return cat.options
    .filter((opt) => {
      if (seen.has(opt.enumValue)) return false;
      seen.add(opt.enumValue);
      return true;
    })
    .map((opt) => ({
      categoryEnumKey: `${cat.categoryKey}:${opt.enumValue}`,
      label: `${cat.label}::${opt.label}`,
      group: cat.label,
    }));
});

/** Grouped options for Ant Design Select */
const GROUPED_OPTIONS = (() => {
  const groups = new Map<string, MessageSelectOption[]>();
  ALL_MESSAGE_OPTIONS.forEach((opt) => {
    const existing = groups.get(opt.group);
    if (existing) {
      existing.push(opt);
    } else {
      groups.set(opt.group, [opt]);
    }
  });
  return Array.from(groups.entries()).map(([groupLabel, opts]) => ({
    label: groupLabel,
    options: opts.map((o) => ({
      label: o.label,
      value: o.categoryEnumKey,
    })),
  }));
})();

const VARIANT_OPTIONS: { label: string; value: MessageVariant }[] = [
  { label: "Normal", value: "normal" },
  { label: "Valued", value: "valued" },
];

export default function SelectLn2Message({
  value,
  onChange,
  disabled = false,
  displayOnly = false,
  categoryFilter = undefined,
  messageFilter = undefined,
  compact = false,
  disableVariantToggle = false,
}: SelectLn2MessageProps) {
  // Parse existing value to extract category:enum and variant
  const parsed = useMemo(() => (value ? parseCompositeKey(value) : null), [value]);

  // Locked callers always start (and stay) on "normal", regardless of what a
  // stale `value` claims — see disableVariantToggle's doc comment.
  const [variant, setVariant] = useState<MessageVariant>(
    disableVariantToggle ? "normal" : (parsed?.variant ?? "normal"),
  );

  // The "category:enumValue" portion of the key (without variant)
  const categoryEnumKey = parsed ? `${parsed.categoryKey}:${parsed.enumValue}` : undefined;

  // When a category or message filter is active, restrict displayed option groups
  const filteredGroupedOptions = useMemo(() => {
    let groups = GROUPED_OPTIONS;

    if (categoryFilter && categoryFilter.length > 0) {
      const labelMap = new Map(LN2_MESSAGE_CATEGORIES.map((c) => [c.categoryKey, c.label]));
      const allowedLabels = new Set(
        categoryFilter.map((k) => labelMap.get(k)).filter((l): l is string => !!l),
      );
      groups = groups.filter((g) => allowedLabels.has(g.label));
    }

    if (messageFilter) {
      const keySet = ln2MessageFilterToKeySet(messageFilter);
      groups = groups
        .map((g) => ({
          ...g,
          options: g.options.filter((o) => keySet.has(o.value)),
        }))
        .filter((g) => g.options.length > 0);
    }

    return groups;
  }, [categoryFilter, messageFilter]);

  /** Build the full composite key from category:enum + variant */
  const buildKey = (catEnum: string, v: MessageVariant): string => {
    const parts = catEnum.split(":");
    if (parts.length !== 2) return "";
    return `${parts[0]}:${v}:${parts[1]}`;
  };

  if (displayOnly) {
    return <span className="generalStyles__width100">{value ? getDisplayName(value) : "--"}</span>;
  }

  return (
    <div style={{ display: "flex", gap: compact ? 2 : 8 }}>
      {/* Main message dropdown: searchable, grouped by category */}
      <Select
        style={{ flex: "1 1 auto", width: "100%" }}
        size="large"
        placeholder={compact ? "Search..." : "Type to search... (e.g. CarCall, Status::, Fault::)"}
        value={categoryEnumKey}
        onChange={(catEnumKey: string) => {
          onChange(buildKey(catEnumKey, variant));
        }}
        options={filteredGroupedOptions}
        disabled={disabled}
        showSearch
        optionFilterProp="label"
        allowClear
        onClear={() => onChange("")}
        virtual
      />
      {/* Variant toggle */}
      <Select
        style={{ width: "200px" }}
        size="large"
        value={variant}
        onChange={(v: MessageVariant) => {
          setVariant(v);
          // If a message is already selected, update the key with the new variant
          if (categoryEnumKey) {
            onChange(buildKey(categoryEnumKey, v));
          }
        }}
        options={VARIANT_OPTIONS}
        disabled={disabled || disableVariantToggle}
      />
    </div>
  );
}
